import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import {
  catchError,
  tap,
  switchMap,
  map,
  filter,
  take,
  finalize,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SecureStorageService } from '../secure-storage.service';
import { EncryptionService } from '../encryption.service';
import {
  AuthResponse,
  LoginRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from '../../models/auth.model';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'x7d9q2r5p3z8m1n6';
  private readonly REFRESH_TOKEN_KEY = 'j4f8h2k6l9p3s7t1';
  private readonly SESSION_KEY = 'session_id';
  private readonly USER_DATA_KEY = 'b5v9c3x7z2n6m1q4';

  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  // Subject to notify components about auth state changes
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  constructor(
    private router: Router,
    private http: HttpClient,
    private secureStorage: SecureStorageService,
    private tokenService: TokenService,
    private encryptionService: EncryptionService
  ) {
    // Initialize auth state after dependencies are injected
    this.checkExistingSession();
  }

  /**
   * Login user with email and password (with mTLS encryption)
   */
  login(credentials: LoginRequest): Observable<AuthResponse> {
    console.log('Login attempt with mTLS encryption');
    
    return this.encryptionService.encryptFields({
      email: credentials.email,
      password: credentials.password
    }).pipe(
      switchMap(encryptedData =>
        this.http.post<AuthResponse>(
          `${environment.baseUrl}/auth/signin`,
          encryptedData
        )
      ),
      tap((response) => {
        this.storeTokens(response);
        this.isLoggedInSubject.next(true);
        console.log('Login successful with mTLS encryption');
      }),
      catchError((error) => {
        console.error('mTLS Login error:', error);
        
        // If mTLS encryption fails due to public key issues, show specific error
        if (error.message?.includes('public key') || error.message?.includes('endpoint')) {
          console.warn('mTLS encryption unavailable - contact system administrator');
          return throwError(
            () => new Error(
              'Secure login is temporarily unavailable. Please contact your system administrator.'
            )
          );
        }
        
        return throwError(
          () =>
            new Error(
              error.error?.message || 'Login failed. Please try again.'
            )
        );
      })
    );
  }

  /**
   * Send forgot password request (with mTLS encryption)
   */
  forgotPassword(request: ForgotPasswordRequest): Observable<ForgotPasswordResponse> {
    return this.encryptionService.encrypt(request.email).pipe(
      switchMap(encryptedEmail =>
        this.http.post<ForgotPasswordResponse>(
          `${environment.baseUrl}/auth/forgot-password`,
          { email: encryptedEmail }
        )
      ),
      tap(() => console.log('Forgot password request sent with mTLS encryption')),
      catchError((error) => {
        console.error('Forgot password error:', error);
        return throwError(
          () =>
            new Error(
              error.error?.message || 'Failed to send reset password email. Please try again.'
            )
        );
      })
    );
  }

  /**
   * Change user password (with mTLS encryption)
   */
  changePassword(request: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    const token = this.getToken();
    if (!token) {
      return throwError(() => new Error('No authentication token found'));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    return this.encryptionService.encryptFields({
      currentPassword: request.currentPassword,
      newPassword: request.newPassword
    }).pipe(
      switchMap(encryptedData =>
        this.http.post<ChangePasswordResponse>(
          `${environment.baseUrl}/auth/change-password`,
          encryptedData,
          { headers }
        )
      ),
      tap(() => console.log('Password changed with mTLS encryption')),
      catchError((error) => {
        console.error('Change password error:', error);
        return throwError(
          () =>
            new Error(
              error.error?.message || 'Failed to change password. Please try again.'
            )
        );
      })
    );
  }

  /**
   * Reset password using token (with mTLS encryption)
   */
  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.encryptionService.encrypt(request.newPassword).pipe(
      switchMap(encryptedPassword =>
        this.http.post<ResetPasswordResponse>(
          `${environment.baseUrl}/auth/reset-password`,
          {
            token: request.token,
            newPassword: encryptedPassword
          }
        )
      ),
      tap(() => console.log('Password reset with mTLS encryption')),
      catchError((error) => {
        console.error('Reset password error:', error);
        return throwError(
          () =>
            new Error(
              error.error?.message || 'Failed to reset password. Please try again.'
            )
        );
      })
    );
  }

  /**
   * Store tokens and user data securely
   */
  private storeTokens(response: AuthResponse): void {
    this.secureStorage.setItem(this.TOKEN_KEY, response.accessToken);
    this.secureStorage.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);
    this.secureStorage.setItem(this.SESSION_KEY, response.session);

    // Store user info separately
    const userData = {
      id: response.id,
      name: response.name,
      email: response.email,
      mobile: response.mobile,
      role: response.role,
      session: response.session,
    };
    this.secureStorage.setItem(this.USER_DATA_KEY, userData);
  }

  /**
   * Get the stored access token
   */
  getToken(): string | null {
    if (!this.secureStorage) {
      return null;
    }
    return this.secureStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get the stored access token (alias for compatibility)
   */
  getAccessToken(): string | null {
    return this.getToken();
  }

  /**
   * Get the stored refresh token
   */
  getRefreshToken(): string | null {
    if (!this.secureStorage) {
      return null;
    }
    return this.secureStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Get the stored session ID
   */
  getSessionId(): string | null {
    if (!this.secureStorage) {
      return null;
    }
    return this.secureStorage.getItem(this.SESSION_KEY);
  }

  /**
   * Get stored user data
   */
  getUserData(): any {
    if (!this.secureStorage) {
      return null;
    }
    return this.secureStorage.getItem(this.USER_DATA_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  /**
   * Check if token is valid and not expired
   */
  private hasValidToken(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * Get user roles from token
   */
  getUserRoles(): string[] {
    const token = this.getToken();
    if (!token) return [];

    return this.tokenService.getUserRoles(token);
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const token = this.getToken();
    if (!token) return false;

    return this.tokenService.hasRole(token, role);
  }

  /**
   * Refresh the access token using the refresh token
   */
  refreshToken(): Observable<boolean> {
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        map(() => true)
      );
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.refreshTokenInProgress = false;
      return of(false);
    }

    let header = new HttpHeaders({
      Authorization: 'Bearer ' + refreshToken,
    });

    return this.http
      .get<RefreshTokenResponse>(
        `${environment.baseUrl}/auth/refresh`,
        { headers: header }
      )
      .pipe(
        tap((response) => {
          this.secureStorage.setItem(this.TOKEN_KEY, response.accessToken);
          if (response.refreshToken) {
            this.secureStorage.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);
          }
          this.isLoggedInSubject.next(true);
          this.refreshTokenSubject.next(true);
        }),
        map(() => true),
        catchError((error) => {
          this.logout();
          return of(false);
        }),
        finalize(() => {
          this.refreshTokenInProgress = false;
        })
      );
  }

  /**
   * Log user out and clear all auth data
   */
  logout(callBackend: boolean = true, shouldRedirect: boolean = true): void {
    if (callBackend && this.hasValidToken()) {
      const token = this.getToken();
      if (token) {
        this.http.get(`${environment.baseUrl}/auth/logout`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }).subscribe({
          complete: () => this.performLogout(shouldRedirect)
        });
      } else {
        this.performLogout(shouldRedirect);
      }
    } else {
      this.performLogout(shouldRedirect);
    }
  }

  /**
   * Perform logout operations
   */
  private performLogout(shouldRedirect: boolean = true): void {
    this.secureStorage.removeItem(this.TOKEN_KEY);
    this.secureStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.secureStorage.removeItem(this.SESSION_KEY);
    this.secureStorage.removeItem(this.USER_DATA_KEY);
    this.isLoggedInSubject.next(false);
    
    if (shouldRedirect) {
      this.router.navigate(['/admin/auth/login']);
    }
  }

  /**
   * Check existing session on app startup
   */
  private checkExistingSession(): void {
    // Just update the auth state based on token validity
    // Don't make HTTP calls during initialization to avoid routing issues
    const isValid = this.hasValidToken();
    this.isLoggedInSubject.next(isValid);
  }

  /**
   * Validate current session with backend
   */
  private validateCurrentSession(): void {
    // Make a simple authenticated request to verify session
    // You can replace this with any protected endpoint that exists in your API
    const token = this.getToken();
    if (token) {
      this.http.get(`${environment.baseUrl}/auth/verify`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).subscribe({
        error: (error) => {
          if (error.status === 401) {
            this.performLogout();
          }
        }
      });
    }
  }

  /**
   * Show session conflict dialog
   */
  showSessionConflictDialog(): void {
    // For now, use alert to avoid circular dependency issues
    // TODO: Implement proper dialog when MatDialog is properly configured
    alert('Session Conflict: Your account has been logged in from another device. This session has been terminated for security reasons.');
    this.router.navigate(['/admin/auth/login']);
  }

  /**
   * Show session expired message
   */
  showSessionExpiredMessage(): void {
    // Using console.log for now since MatSnackBar is not available
    // You can replace this with your preferred notification method
    console.warn('Your session has expired. Please log in again.');
    
    // Alternative: Show an alert
    alert('Your session has expired. Please log in again.');
  }

  /**
   * Get user info from token
   */
  getUserInfo(): any {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        userId: payload.sub,
        roles: payload.roles,
        session: payload.session
      };
    } catch {
      return null;
    }
  }
}
